import React from "react";
import { LegalLayout } from "@/components/site/LegalLayout";
import { COMPANY } from "@/lib/constants";

const CANONICAL = "https://alldaypowerwashing.com/privacy";

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How we handle your data"
      description="Privacy Policy for All Day Power Washing — what data we collect, how we use it, SMS consent, and your rights."
      canonical={CANONICAL}
      testId="privacy-page"
    >
      <p className="text-slate-400 text-sm">Last updated: February 2026</p>

      <p>
        {COMPANY.name} ("Company", "we", "us", or "our") respects your privacy.
        This Privacy Policy explains what information we collect when you use
        our website ({COMPANY.website || "alldaypowerwashing.com"}), request a
        quote, or schedule services, how we use that information, and the
        choices you have. By using our website or submitting a quote, you
        agree to this Privacy Policy.
      </p>

      <h2>1. Information We Collect</h2>
      <p>We collect only the information you voluntarily provide to us:</p>
      <ul>
        <li>
          <strong>Contact details</strong> — name, phone number, email address,
          and service address submitted through the quote form.
        </li>
        <li>
          <strong>Service details</strong> — the service you are requesting
          (e.g. power washing, concrete cleaning, fleet wash), property square
          footage tier, and any notes you provide.
        </li>
        <li>
          <strong>Payment information</strong> — handled by our third-party
          payment processor when you pay an invoice. We do not store full card
          numbers on our servers.
        </li>
        <li>
          <strong>Photos and job documentation</strong> — "before and after"
          photos of your property that we may capture for estimates, work
          records, and (with your consent) marketing.
        </li>
        <li>
          <strong>Site analytics</strong> — privacy-friendly, aggregate page
          view and referrer data via Plausible.io. Plausible does not use
          cookies and does not collect personal data or cross-site tracking
          identifiers.
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Respond to your quote request and schedule service.</li>
        <li>
          Send you transactional updates (appointment confirmations, arrival
          windows, invoices, follow-ups) by phone, SMS, or email.
        </li>
        <li>Maintain customer records for accounting and warranty purposes.</li>
        <li>
          Improve our services and website performance (aggregated analytics
          only).
        </li>
        <li>Comply with legal obligations (tax records, insurance, subpoenas).</li>
      </ul>

      <h2>3. SMS / Text Message Consent</h2>
      <p>
        By submitting your phone number through our quote form, you consent to
        receive transactional SMS text messages from {COMPANY.name} related to
        your quote, scheduling, service confirmations, and service updates.
      </p>
      <ul>
        <li>Message and data rates may apply.</li>
        <li>Message frequency varies based on the status of your job.</li>
        <li>
          Reply <strong>STOP</strong> at any time to opt out of further SMS
          messages from us.
        </li>
        <li>
          Reply <strong>HELP</strong> for support, or contact us at{" "}
          {COMPANY.phone} or {COMPANY.email}.
        </li>
        <li>
          <strong>
            We do not share your phone number or SMS opt-in consent with any
            third party for marketing or promotional purposes.
          </strong>
        </li>
      </ul>

      <h2>4. How We Share Information</h2>
      <p>
        We do not sell, rent, or trade your personal information. We share
        information only with trusted service providers who help us operate
        our business, and only to the extent needed to do their job, including:
      </p>
      <ul>
        <li>
          <strong>Twilio</strong> — to deliver SMS notifications related to
          your quote and service.
        </li>
        <li>
          <strong>Payment processors</strong> — to process invoices you pay by
          credit card or ACH.
        </li>
        <li>
          <strong>Hosting and database providers</strong> — to store quote
          submissions and customer records securely.
        </li>
        <li>
          <strong>Law enforcement or regulators</strong> — if required by law,
          subpoena, or court order.
        </li>
      </ul>

      <h2>5. Cookies &amp; Tracking</h2>
      <p>
        Our website uses minimal tracking. We use Plausible Analytics, a
        cookie-less, privacy-friendly analytics service, to understand overall
        site traffic. Plausible does not set tracking cookies, does not
        fingerprint visitors, and does not share data with third parties. We
        may use strictly-necessary cookies to keep admin sessions signed in,
        but we do not use advertising, retargeting, or cross-site tracking
        cookies on this site.
      </p>

      <h2>6. Data Retention</h2>
      <p>
        Quote submissions and customer records are retained for as long as
        necessary to provide services, maintain business records, and comply
        with legal obligations (typically up to 7 years for tax records).
        Marketing photos and reviews may be retained indefinitely unless you
        request removal.
      </p>

      <h2>7. Your Rights &amp; Choices</h2>
      <ul>
        <li>
          <strong>Access / Correction / Deletion</strong> — You may request a
          copy of the personal information we hold about you, or request
          correction or deletion, by emailing {COMPANY.email}. We will respond
          within 30 days.
        </li>
        <li>
          <strong>Opt-out of SMS</strong> — Reply <strong>STOP</strong> to any
          SMS message from us.
        </li>
        <li>
          <strong>Opt-out of email</strong> — Use the unsubscribe link in any
          non-transactional email, or email us directly.
        </li>
        <li>
          <strong>California residents (CCPA)</strong> — You have the right to
          know what personal information we collect about you, to request
          deletion, and to not be discriminated against for exercising those
          rights. We do not sell personal information.
        </li>
      </ul>

      <h2>8. Children's Privacy</h2>
      <p>
        Our services are directed to adults. We do not knowingly collect
        personal information from children under 13. If you believe a child
        has provided us information, please contact us and we will delete it.
      </p>

      <h2>9. Security</h2>
      <p>
        We use reasonable administrative, technical, and physical safeguards
        to protect your personal information (encrypted transport, access
        controls, hashed passwords for admin accounts). No internet
        transmission is ever 100% secure, however, and we cannot guarantee
        absolute security.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy at any time. The "Last updated"
        date at the top of this page reflects the most recent revision. If
        we make material changes, we will post a notice on the site.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        Questions about this Privacy Policy or about the data we hold on you?
        Email{" "}
        <a href={`mailto:${COMPANY.email}`} className="text-[#1E88E5] hover:text-[#42A5F5]">
          {COMPANY.email}
        </a>{" "}
        or call/text {COMPANY.phone}. You can also write to us at{" "}
        {COMPANY.address.full}.
      </p>
    </LegalLayout>
  );
}
